// ============================================
// RESET PASSWORD PAGE
// ============================================

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { useState } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/reset-password/$token' as any)({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { token } = Route.useParams() as { token: string };
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetPasswordSchema = z.object({
    password: z
      .string()
      .min(8, t('validation.passwordMin8'))
      .regex(/[A-Z]/, t('validation.uppercase'))
      .regex(/[a-z]/, t('validation.lowercase'))
      .regex(/[0-9]/, t('validation.number')),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('validation.passwordMismatch'),
    path: ['confirmPassword'],
  });

  type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

  const resetPasswordMutation = useMutation({
    mutationFn: (data: { token: string; password: string; confirmPassword: string }) => 
      authApi.resetPassword(data),
    onSuccess: () => {
      setIsSuccess(true);
      setTimeout(() => {
        navigate({ to: '/login' });
      }, 3000);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    resetPasswordMutation.mutate({
      token,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-maritime-600 to-maritime-800 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelSubtitle.company')}</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {isSuccess ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('resetPassword.successTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('resetPassword.successMessage')}
              </p>
              <Link to="/login" className="btn-primary w-full">
                {t('resetPassword.loginNow')}
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('resetPassword.title')}</h2>
              <p className="text-gray-600 text-sm mb-6">
                {t('resetPassword.subtitle')}
              </p>

              {/* Error Message */}
              {resetPasswordMutation.isError && (
                <div className="mb-4 p-3 rounded-maritime bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {resetPasswordMutation.error instanceof Error
                      ? resetPasswordMutation.error.message
                      : t('resetPassword.errorDefault')}
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="password" className="label">
                    {t('resetPassword.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
                      placeholder="••••••••"
                      {...register('password')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    {t('resetPassword.confirmPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`input pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
                      placeholder="••••••••"
                      {...register('confirmPassword')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="p-3 rounded-maritime bg-gray-50 border border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">{t('resetPassword.requirements.title')}</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• {t('resetPassword.requirements.min8')}</li>
                    <li>• {t('resetPassword.requirements.uppercase')}</li>
                    <li>• {t('resetPassword.requirements.lowercase')}</li>
                    <li>• {t('resetPassword.requirements.number')}</li>
                  </ul>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('resetPassword.submitting')}
                    </>
                  ) : (
                    t('resetPassword.submit')
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: 2025 })}
        </p>
      </div>
    </div>
  );
}
